import clsx from 'clsx';
import { useRef, useState, type ReactNode } from 'react';
import type { Settings, SettingsDTO } from '@wd/shared';
import { useSettingsActions } from '../../lib/queries';
import { Icon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { Toggle } from '../../ui/primitives';

// 设置页的通用件：标题、分组、一行设置（左说明右控件）、下拉选择。

export function Head({ title, desc, right }: { title: string; desc?: ReactNode; right?: ReactNode }) {
  return (
    <div className="st-head">
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        <h1 className="t-page-title">{title}</h1>
        <span className="flex1" />
        {right}
      </div>
      {desc && <p className="st-desc">{desc}</p>}
    </div>
  );
}

export function Group({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="st-group">
      <div className="st-group-head">
        <span className="name">{title}</span>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Row({ label, desc, tag, children }: { label: ReactNode; desc?: ReactNode; tag?: ReactNode; children?: ReactNode }) {
  return (
    <div className="st-row">
      <div className="l">
        <span className="t">
          {label}
          {tag && <span className="st-tag">{tag}</span>}
        </span>
        {desc && <span className="d">{desc}</span>}
      </div>
      {children}
    </div>
  );
}

export interface Option<T> {
  value: T;
  label: string;
}

/** 下拉选择：按钮显示当前值，点开是菜单 */
export function Choice<T extends string | number | boolean>({
  value,
  options,
  onChange,
  disabled,
  width = 200,
}: {
  value: T;
  options: Option<T>[];
  onChange: (v: T) => void;
  disabled?: boolean;
  width?: number;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <>
      <button ref={ref} type="button" className={clsx('st-select', disabled && 'ro')} disabled={disabled} onClick={() => setOpen(!open)}>
        <span>{current?.label ?? String(value)}</span>
        {!disabled && <Icon name="chevD" size={12} />}
      </button>
      <Popover anchor={ref} open={open} onClose={() => setOpen(false)} width={width} align="end">
        <MenuList
          items={options.map((o) => ({ value: String(o.value), label: o.label }))}
          selected={String(value)}
          onPick={(v) => {
            setOpen(false);
            const picked = options.find((o) => String(o.value) === v);
            if (picked && picked.value !== value) onChange(picked.value);
          }}
        />
      </Popover>
    </>
  );
}

export function timeOptions(from: string, to: string, stepMin = 30): Option<string>[] {
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
  const out: Option<string>[] = [];
  for (let m = toMin(from); m <= toMin(to); m += stepMin) {
    const t = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    out.push({ value: t, label: t });
  }
  return out;
}

export function rangeOptions(from: number, to: number, label: (n: number) => string): Option<number>[] {
  return Array.from({ length: to - from + 1 }, (_, i) => ({ value: from + i, label: label(from + i) }));
}

/** 读写一项设置：显示“明天起生效”的待定值，非管理员只读 */
export function useSetting<K extends keyof Settings>(dto: SettingsDTO, key: K) {
  const { update } = useSettingsActions();
  const pending = key in dto.pending;
  const value = (pending ? dto.pending[key] : dto.current[key]) as Settings[K];
  return {
    value,
    pending,
    disabled: !dto.canEdit || update.isPending,
    set: (v: Settings[K]) => update.mutate({ [key]: v } as Partial<Settings>),
  };
}

export function SettingChoice<K extends keyof Settings>({
  dto,
  k,
  options,
  width,
}: {
  dto: SettingsDTO;
  k: K;
  options: Option<Settings[K] & (string | number | boolean)>[];
  width?: number;
}) {
  const s = useSetting(dto, k);
  return <Choice value={s.value as Settings[K] & (string | number | boolean)} options={options} onChange={(v) => s.set(v)} disabled={!dto.canEdit} width={width} />;
}

export function SettingToggle<K extends keyof Settings>({ dto, k, label }: { dto: SettingsDTO; k: K; label: string }) {
  const s = useSetting(dto, k);
  if (!dto.canEdit) return <span className="st-ro">{s.value ? '开启' : '关闭'}</span>;
  return <Toggle on={!!s.value} label={label} onChange={(v) => s.set(v as Settings[K])} />;
}

